import { Box, Tab, Tabs, Badge } from '@mui/material'
import { useEffect } from 'react'
import type { ChangeEvent, JSX } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Outlet,
  useNavigate,
  useLocation,
  useLoaderData,
  useRevalidator,
} from 'react-router-dom'

import { layoutStyles, theme } from '@/utils/theme'

const FotaJobPage = (): JSX.Element => {
  const { t } = useTranslation('app')
  const navigate = useNavigate()
  const location = useLocation()
  const revalidator = useRevalidator()
  const { errorCount, installedCount, updateReadyCount } = useLoaderData()

  // Get current tab from location path
  const currentTab = location.pathname.split('/').pop() ?? 'create'

  // Auto-refresh counts every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      revalidator.revalidate()
    }, 10000)

    // Cleanup interval on component unmount
    return () => clearInterval(interval)
  }, [revalidator])

  const handleTabChange = (event: ChangeEvent<unknown>, newValue: number) => {
    const tabRoutes = ['create', 'update-ready', 'failed', 'installed', 'all']
    navigate(`/v2/main/device/fota-management/${tabRoutes[newValue]}`)
  }

  const getTabValue = () => {
    const tabRoutes = ['create', 'update-ready', 'failed', 'installed', 'all']
    const index = tabRoutes.indexOf(currentTab)
    return index === -1 ? 0 : index
  }

  return (
    <Box
      sx={{
        ...layoutStyles.pageContainer,
        mt: 0,
        p: 2,
      }}
      id={'page container box'}
    >
      <Box sx={{ ...layoutStyles.tabsContainer }} id={'tab container box'}>
        <Tabs
          value={getTabValue()}
          onChange={handleTabChange}
          aria-label="fota job tabs"
          variant="standard"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.secondary.main,
              height: 3,
            },
            '& .MuiTabs-flexContainer': {
              justifyContent: 'flex-start',
            },
            mb: 2,
          }}
        >
          <Tab
            label={t('device.fotaJobs.createFota')}
            id={`tab-0`}
            aria-controls={`tabpanel-0`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <Tab
            label={
              <Badge badgeContent={updateReadyCount} color="primary">
                {t('device.fotaJobs.updateReady')}
              </Badge>
            }
            id={`tab-1`}
            aria-controls={`tabpanel-1`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <Tab
            label={
              <Badge badgeContent={errorCount} color="error">
                {t('device.fotaJobs.failed')}
              </Badge>
            }
            id={`tab-2`}
            aria-controls={`tabpanel-2`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <Tab
            label={
              <Badge badgeContent={installedCount} color="success">
                {t('device.fotaJobs.installed')}
              </Badge>
            }
            id={`tab-3`}
            aria-controls={`tabpanel-3`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <Tab
            label={t('device.fotaJobs.allJobs')}
            id={`tab-4`}
            aria-controls={`tabpanel-4`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
        </Tabs>
        <Outlet />
      </Box>
    </Box>
  )
}

export default FotaJobPage
